import React from 'react';
import { CheckCircle, AlertTriangle, Pause, Play, Edit, Trash2 } from 'lucide-react';

const MonitorCard = ({ monitor, onEdit, onDelete, onToggleStatus }) => {
  const getStatusColor = () => {
    if (monitor.status !== 'active') return 'gray';
    return monitor.is_up ? 'green' : 'red';
  };

  const getStatusIcon = () => {
    if (monitor.status !== 'active') return Pause;
    return monitor.is_up ? CheckCircle : AlertTriangle;
  };

  const StatusIcon = getStatusIcon();
  const statusColor = getStatusColor();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 min-w-0 flex-1 mr-3">
          <div className={`p-2 rounded-lg flex-shrink-0 ${
            statusColor === 'green' ? 'bg-green-50 text-green-600' :
            statusColor === 'red' ? 'bg-red-50 text-red-600' :
            'bg-gray-50 text-gray-600'
          }`}>
            <StatusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{monitor.name}</h3>
            <p className="text-xs sm:text-sm text-gray-500 break-all">{monitor.url}</p>
          </div>
        </div>
        
        {/* Mobile: Stack buttons vertically, Desktop: Horizontal */}
        <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2 flex-shrink-0">
          <button
            onClick={() => onToggleStatus(monitor)}
            className={`p-2 rounded-lg transition-colors ${
              monitor.status === 'active' 
                ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
            title={monitor.status === 'active' ? 'Pause monitoring' : 'Resume monitoring'}
          >
            {monitor.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onEdit(monitor)}
            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(monitor)}
            className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
        <div>
          <p className="text-gray-500 text-xs sm:text-sm">Status</p>
          <p className={`font-medium text-sm sm:text-base ${
            statusColor === 'green' ? 'text-green-600' :
            statusColor === 'red' ? 'text-red-600' :
            'text-gray-600'
          }`}>
            {monitor.status !== 'active' ? 'Paused' : monitor.is_up ? 'Online' : 'Offline'}
          </p>
        </div>
        <div>
          <p className="text-gray-500 text-xs sm:text-sm">Response</p>
          <p className="font-medium text-gray-900 text-sm sm:text-base">
            {monitor.response_time ? `${Math.round(monitor.response_time * 1000)}ms` : '-'}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <p className="text-gray-500 text-xs sm:text-sm">Interval</p>
          <p className="font-medium text-gray-900 text-sm sm:text-base">{monitor.check_interval / 60}min</p>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        Last checked: {monitor.last_checked ? new Date(monitor.last_checked).toLocaleString() : 'Never'}
      </div>
    </div>
  );
};

export default MonitorCard;
